/**
 * AntD <Button> whose `loading`/`disabled` reacts to Redux ScrapeJob state
 * for the given (patientId, operationType). Lets a card render multiple
 * concurrent scrape buttons without one in-flight job blocking another.
 *
 * Falls back to the legacy `disabled`/`loading` props when there's no
 * active scrape recorded — so the legacy 2s-poll UI keeps working until
 * PR #6 deletes it.
 *
 * react-redux 6.x — uses connect().
 */

import React from "react";
import { connect } from "react-redux";
import { Button } from "antd";

import { armNextDispatchId, newDispatchId } from "../../utils/dispatchTracking";
import { debugLog } from "../../utils/debugLog";

const ACTIVE_STATES = new Set(["queued", "claimed", "running", "awaiting_2fa"]);

function ScrapeJobAwareButtonInner(props) {
  const { activeForType, anyOtherActive, loading, disabled, children, onClick, patientId, operationType, ...rest } = props;
  // Reactive overrides from Redux: spin if there's an active job for THIS
  // operation type; do NOT auto-disable just because some other type is
  // running on the same patient — that's exactly the constraint we're
  // dropping. Caller can still pass `disabled` for capability gating.
  const effectiveLoading = loading || activeForType;

  const handleClick = (e) => {
    // Mint a new correlation id for this click. The next outgoing POST
    // (within 5s) will carry it as `X-Dispatch-Id`. Backend logs the same
    // id in `view_entry` / `view_dispatched`, so the FE click and BE
    // dispatch are joinable in scrape_flow.log.
    const dispatchId = newDispatchId();
    armNextDispatchId(dispatchId);
    debugLog.info("button", "scrape_button_click", {
      dispatch_id: dispatchId,
      patient_id: patientId,
      operation_type: operationType,
    });
    if (typeof onClick === "function") {
      onClick(e);
    }
  };

  return (
    <Button {...rest} onClick={handleClick} loading={effectiveLoading} disabled={disabled}>
      {children}
    </Button>
  );
}

const mapStateToProps = (state, ownProps) => {
  const ids = (state.scrapeJobs && state.scrapeJobs.idsByPatient[ownProps.patientId]) || [];
  let activeForType = false;
  let anyOtherActive = false;
  for (let i = 0; i < ids.length; i++) {
    const j = state.scrapeJobs.byId[ids[i]];
    if (!j || !ACTIVE_STATES.has(j.state)) continue;
    if (j.operation_type === ownProps.operationType) activeForType = true;
    else anyOtherActive = true;
  }
  return { activeForType, anyOtherActive };
};

export default connect(mapStateToProps)(ScrapeJobAwareButtonInner);
